import itiriri from 'itiriri';
import React, { useState } from 'react';
import { FacilityRoleDto, LoginType, OidcProviderDto, OidcProviderType, RoleDto, UserOnboardingReq } from "server-openapi";
import { CsvParser } from '../../../../../components/CsvParser';
import { ValidationMessages } from '../../../../../core/utils/validationMessages';
import { Grid } from '../../../../../kit/Grid';
import { Intent } from '../../../../../kit/Theme/Theme';
import { DateUtils } from '../../../../../core/utils/dateUtils';
import { useSyncCenter } from '../../../../../syncstream/SyncCenterProvider';
import { useStore } from '../../../../../core/storage/hooks/UseStore';
import _ from 'lodash';
import { apis } from '../../../../../core/mrs/apis';
import { useAsync } from '../../../../../kit/hooks/UseAsync';

interface IProps {
  facilityGroupId: number;
  roles: RoleDto[];
  // NOTE: This is temporary as change to new permissions structure will eventually remove the old usage of user role assignments
  facilityRoles: FacilityRoleDto[];
  useUserPermissionsFromHs: boolean;
  usersSelected: (usersToImport: UserOnboardingReq[], allowedIdpProviders: OidcProviderDto[]) => void;
}

export function ImportUsersDialogContent(props: IProps) {
  const [isLoading, setIsLoading] = useState(false);

    const services = useSyncCenter();
    const facilityGroupConfigurationStore = useStore(services.facilityGroupConfigurations.store).store;
    const storedFacilityGroupConfiguration = facilityGroupConfigurationStore.get(props.facilityGroupId.toString());

    // Fall back to the server when the facility group configuration hasn't been synced into the store
    const fetchedFacilityGroupConfiguration = useAsync(async () => {
      if (storedFacilityGroupConfiguration) {
        return undefined;
      }
      return (await apis.facilityGroups.facilityGroupsGetFacilityGroupConfiguration(props.facilityGroupId)).data;
    }, [props.facilityGroupId]);

    const facilityGroupConfiguration = storedFacilityGroupConfiguration ?? fetchedFacilityGroupConfiguration.value;
    const isLoadingConfiguration = !storedFacilityGroupConfiguration && fetchedFacilityGroupConfiguration.loading;
    const loginTypes = facilityGroupConfiguration?.loginTypes;
    const allowedIdpProviders = facilityGroupConfiguration?.allowedIdpProviders ?? [];

    return (
    <>
      <Grid gap={1}>
        <h2>Import users from a CSV file</h2>
        <CsvParser
          intent={Intent.Secondary}
          fullWidth
          columnRules={[
            { name: 'FirstName', match: /^[a-zA-Z0-9 ,.'-]+$/i },
            { name: 'FamilyName', match: /^[a-zA-Z0-9 ,.'-]+$/i },
            { name: 'Email', match: ValidationMessages.email.regex },
            { name: 'Designation', match: /^[a-zA-Z0-9 ,.'-]+$/i },
            { name: 'LoginType', match: loginTypes ? [...loginTypes, ''] : undefined },
            { name: 'OidcProvider', match: [...allowedIdpProviders.map((p) => p.description!), ''] },
            ...(props.useUserPermissionsFromHs
              ? [{ name: 'Roles', match: props.facilityRoles.map((r) => r.name!), isValueCommaSeparated: true }]
              : [{ name: 'Roles', match: props.roles.map((r) => r.name!), isValueCommaSeparated: true }]),
          ]}
          onChange={async (data) => {
            setIsLoading(true);

            props.usersSelected(
              importUsers(data, props.facilityGroupId, props.roles, props.facilityRoles, props.useUserPermissionsFromHs, allowedIdpProviders),
              allowedIdpProviders,
            );
          }}
          disabled={ isLoading || isLoadingConfiguration }
        />
      </Grid>
    </>
  );
}

function importUsers(
  usersToImport: Record<string, string>[],
  facilityGroupId: number,
  roles: RoleDto[],
  facilityRoles: FacilityRoleDto[],
  useUserPermissionsFromHs: boolean,
  allowedIdpProviders: OidcProviderDto[],
): UserOnboardingReq[] {
  return itiriri(usersToImport)
    .map((u) => {
      const firstName = u.FirstName.trim();
      const familyName = u.FamilyName.trim();
      const designation = u.Designation.trim();
      const oidcProvider = u.OidcProvider.trim();
      const matchedIdpProvider = oidcProvider
        ? allowedIdpProviders.find((p) => p.description?.trim().toLowerCase() === oidcProvider.toLowerCase())
        : allowedIdpProviders.find((p) => p.type === OidcProviderType.AzureAdb2C);
      let loginType: LoginType | undefined = LoginType[_.startCase(_.toLower(u.LoginType)) as keyof typeof LoginType];
      if (matchedIdpProvider) {
        if (matchedIdpProvider.type === OidcProviderType.AzureAdb2C) {
          loginType = loginType ?? LoginType.Local;
        } else {
          loginType = LoginType.NotUsed;
        }
      }
      const selectedFacilityRoles = (u.Roles ?? '')
        .split(',')
        .map((val) => val.trim())
        .filter(Boolean);

      return {
        email: u.Email,
        firstName,
        familyName,
        designation,
        loginType,
        oidcProviderId: matchedIdpProvider?.id,
        // NOTE: This is temporary as change to new permissions structure will eventually remove the old usage of user role assignments
        facilityRoleAssignments: useUserPermissionsFromHs && facilityRoles?.length
          ? facilityRoles
              .filter((r) => selectedFacilityRoles.includes(r.name!))
              .map((r) => ({
                facilityRoleId: r.hsId!,
                facilityRoleName: r.name!,
                facilityId: facilityGroupId,
                startAtInclusive: DateUtils.fromDate(new Date()),
                active: true,
              }))
          : null,
        userRoleAssignments: !useUserPermissionsFromHs && roles?.length
          ? roles
          .filter((r) =>
            u.Roles.split(',')
              .map((val) => val.trim())
              .includes(r.name!),
          )
          .map((r) => {
            return { roleUrn: r.urn!, startAt: DateUtils.fromDate(new Date()), softDelete: false };
          })
          : null,
      };
    })
    .toArray();
}
